import classNames from 'classnames';
import type { AriaAttributes } from 'react';
import * as React from 'react';
import { getContentLocale } from '~/Features/Configuration/Utils/languageUtils';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { PillTag } from '~/Features/Partials/PillTag/PillTag';
import type { SubjectBaseProps } from '~/Features/StudyPage/Common/Interfaces/SubjectBaseProps';
import { Button } from '~/Partials/Button/Button';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { assertNever } from '~/Partials/Utils/typeUtils';
import { useClickProxyAllowingTextSelection } from '~/Utils/Hooks/useClickProxyAllowingTextSelection';
import styles from './SubjectCard.module.css';

export interface SubjectCardProps extends SubjectBaseProps {
    onClick?: () => void;
    className?: string;
    additionalAriaProps?: AriaAttributes;
}

export const SubjectCard: React.FC<SubjectCardProps> = props => {
    const contextualTheme = useTheme();
    const {
        subjectName,
        subjectCode,
        points,
        translations,
        subjectCategory,
        className,
        onClick,
        additionalAriaProps,
        theme = contextualTheme,
    } = props;

    const { withRoundedCorners } = theme;
    const locale = getContentLocale();

    const getFooterPillTag = () => {
        const pillTagBase = {
            className: styles.PillTag,
            size: 'small',
        } as const;

        switch (subjectCategory) {
            case 'obligatory':
                return null;
            case 'specialization':
                return (
                    <PillTag {...pillTagBase} variant="brightRed" type="solid" fontColor="light">
                        {translations.specialization}
                    </PillTag>
                );
            case 'elective':
                return (
                    <PillTag {...pillTagBase} variant="turquoise" type="outline" fontColor="light">
                        {translations.elective}
                    </PillTag>
                );
            default:
                assertNever(subjectCategory);
        }
    };
    const { clickableRef, onProxyMouseDown, onProxyMouseUp } = useClickProxyAllowingTextSelection<HTMLButtonElement>();

    return (
        <div
            role="none"
            className={classNames(styles.SubjectCard, withRoundedCorners && styles['Variant--rounded'], className)}
            onMouseDown={onProxyMouseDown}
            onMouseUp={onProxyMouseUp}
            tabIndex={-1}
        >
            <h3 className={styles.Header}>
                <Button
                    className={styles.Button}
                    {...additionalAriaProps}
                    ref={clickableRef}
                    onClick={onClick}
                    type={ButtonType.Clear}
                >
                    <div className={styles.Inner}>
                        <span className={styles.Name}>{subjectName}</span>
                        <span className={styles.Code}>{subjectCode}</span>
                    </div>
                </Button>
            </h3>
            <div className={styles.Footer}>
                {getFooterPillTag()}
                <PillTag
                    type="solid"
                    className={classNames(styles.PillTag, styles['PillTag--points'])}
                    variant="grey"
                    fontColor="light"
                    size="small"
                >
                    {points.toLocaleString(locale)} {translations.pointsUnit}
                </PillTag>
            </div>
        </div>
    );
};
